import AudioInputList from './AudioInputList/AudioInputList';
import AudioOutputList from './AudioOutputList/AudioOutputList';
import { Box } from '@mui/material';
import { ModalDialog, Toggle } from '@dtsl/react';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import VideoInputList from './VideoInputList/VideoInputList';
import MaxGalleryViewParticipants from './MaxGalleryViewParticipants/MaxGalleryViewParticipants';
import { useTranslation } from 'react-i18next';
import { useAppState } from '../../state';
import { useKrispToggle } from '../../hooks/useKrispToggle/useKrispToggle';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';

const useStyles = makeStyles(() => ({
  container: {
    '& .inputSelect': {
      width: 'calc(100% - 35px)',
    },
    '& svg': {
      position: 'relative',
      bottom: '8px',
    },
  },
  headline: {
    marginBottom: '1em',
  },
  listSection: {
    margin: '2em 0 0.8em',
    '&:first-child': {
      margin: '1em 0 2em 0',
    },
  },
  listSubSection: {
    margin: '1em 0 0',
  },
}));

export default function DeviceSelectionDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { isKrispEnabled, isKrispInstalled } = useAppState();
  const { toggleKrisp } = useKrispToggle();
  const { isAcquiringLocalTracks } = useVideoContext();

  return (
    <ModalDialog isVisible={open} closeModal={onClose} size="md">
      <ModalDialog.Header title={t('ROOM.SETTINGS')} />
      <ModalDialog.Body>
        {/* this style is a copy paste from design system */}
        <div className={clsx(classes.container, 'sib-typo_text-body')} style={{ padding: '24px 32px' }}>
          <div className={classes.listSection}>
            <p
              className={clsx(classes.headline, 'sib-typo_heading--secondary_interactive', 'sib-typo_heading-size--sm')}
            >
              {t('ROOM.CAM_SETTINGS')}
            </p>
            <VideoInputList />
          </div>
          <div className={classes.listSection}>
            <p
              className={clsx(classes.headline, 'sib-typo_heading--secondary_interactive', 'sib-typo_heading-size--sm')}
            >
              {t('ROOM.MIC_SETTINGS')}
            </p>
            <AudioInputList />
            <div className={classes.listSubSection}>
              {isKrispInstalled && (
                <>
                  <Toggle
                    id="video-calls-studio-sound-toggle"
                    checked={isKrispEnabled}
                    onChange={toggleKrisp}
                    disabled={isAcquiringLocalTracks}
                    labelText={t('ROOM.STUDIO_SOUND')}
                  />
                  <small>{t('ROOM.STUDIO_SOUND_DESC')}</small>
                </>
              )}
            </div>
          </div>
          <div className={classes.listSection}>
            <AudioOutputList />
          </div>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <div className={classes.listSection}>
              <p
                className={clsx(
                  classes.headline,
                  'sib-typo_heading--secondary_interactive',
                  'sib-typo_heading-size--sm'
                )}
              >
                {t('ROOM.GALLERY_SETTINGS')}
              </p>
              <MaxGalleryViewParticipants />
            </div>
          </Box>
        </div>
      </ModalDialog.Body>
      <ModalDialog.Footer
        primaryAction={{
          id: 'video-calls-device-selection-save',
          label: t('ROOM.DONE'),
          variant: 'primary',
          onClick: onClose,
        }}
        secondaryAction={{
          id: 'video-calls-device-selection-cancel',
          label: t('ROOM.CANCEL'),
          variant: 'tertiary',
          onClick: onClose,
        }}
      />
    </ModalDialog>
  );
}
